import { observer } from 'mobx-react-lite'
import { ColumnDef } from '@tanstack/react-table'
import { createContext, ReactElement, ReactNode, useContext } from 'react'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import { Modal } from 'app/components/Modal'
import { ItemsTitle } from 'app/components/shared/styled/ItemsTitle'
import SimpleTable from 'app/components/table/SimpleTable'
import { FilterableColumn } from 'app/filter.utils'
import type { ContactJson } from 'app/types/entities'

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

type ContactListModalContextValue = {
  contacts: ContactJson[]
  selectedContact?: ContactJson
  drawerOpen: boolean
  isNavigating: boolean
  onContactClick: (contactId: string) => void
  onCloseDrawer: () => void
  onContactChange: (contact: ContactJson) => void
  nextContact: () => void
  prevContact: () => void
}

const ContactListModalContext = createContext<ContactListModalContextValue | null>(null)

function useContactListModalContext(): ContactListModalContextValue {
  const ctx = useContext(ContactListModalContext)
  if (!ctx) throw new Error(`ContactListModal compound components must be used within <ContactListModal.Root>`)
  return ctx
}

// ---------------------------------------------------------------------------
// Root -- provides context + modal shell
// ---------------------------------------------------------------------------

type RootProps = Omit<ContactListModalContextValue, `isNavigating`> & {
  open: boolean
  onClose: () => void
  isNavigating?: boolean
  children: ReactNode
}

const Root = observer(function Root({
  open,
  onClose,
  isNavigating = false,
  children,
  ...contextValue
}: RootProps): ReactElement {
  return (
    // oxlint-disable-next-line react/jsx-no-constructed-context-values -- context value is intentionally assembled here
    <ContactListModalContext.Provider value={{ ...contextValue, isNavigating }}>
      <Modal open={open} onClose={onClose} isFullScreen hideTitle removePadding>
        <div x-component="ContactListModal" className="flex h-[100vh] max-h-[100vh] flex-col overflow-hidden px-5">
          <div className="flex h-full w-full">{children}</div>
        </div>
      </Modal>
    </ContactListModalContext.Provider>
  )
})

// ---------------------------------------------------------------------------
// Header — title + count badge + close button
// ---------------------------------------------------------------------------

type HeaderProps = {
  title: string
  subtitle?: string
  count: number
  onClose: () => void
}

function Header({ title, subtitle, count, onClose }: HeaderProps): ReactElement {
  return <ItemsTitle title={title} subtitle={subtitle} items={count} onClose={onClose} />
}

// ---------------------------------------------------------------------------
// Table — search + filters + data table (no Edit Columns)
// ---------------------------------------------------------------------------

type TableProps = {
  columns: ColumnDef<ContactJson, any>[]
  filterableColumns?: FilterableColumn[]
  onSearchChange?: (value: string) => void
  onFiltersChange?: (filters: string) => void
}

function Table({ columns, filterableColumns, onSearchChange, onFiltersChange }: TableProps): ReactElement {
  const { contacts, selectedContact } = useContactListModalContext()

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <SimpleTable
        data={contacts}
        columns={columns}
        filterableColumns={filterableColumns}
        meta={{ getRowStyle: (row) => ((row as ContactJson)?.id === selectedContact?.id ? `table-selected-row` : ``) }}
        onSearchValueChange={onSearchChange}
        onFiltersChange={onFiltersChange}
        enableDeepLinking={false}
        hideFooter
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Drawer — contact detail side panel
// ---------------------------------------------------------------------------

function Drawer(): ReactElement {
  const { selectedContact, drawerOpen, isNavigating, onCloseDrawer, onContactChange, nextContact, prevContact } =
    useContactListModalContext()

  return (
    <ContactDrawer
      contact={selectedContact}
      openDrawer={drawerOpen}
      onClose={onCloseDrawer}
      onChange={onContactChange}
      nextContact={nextContact}
      previousContact={prevContact}
      isNavigating={isNavigating}
    />
  )
}

// ---------------------------------------------------------------------------
// Public API — Compound Component
// ---------------------------------------------------------------------------

export const ContactListModal = {
  Root,
  Header,
  Table,
  Drawer,
}
